import { type TypedDocumentNode, gql, useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import {
  NotificationPreferenceChannel,
  SportOrShared,
} from '__generated__/globalTypes';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { LabelM } from 'atoms/typography';

import { NotificationPreference } from '../NotificationPreference';
import { Section } from '../Section';
import type {
  NotificationSection_notificationSection,
  UpdateSectionNotificationPreferencesMutation,
  UpdateSectionNotificationPreferencesMutationVariables,
} from './__generated__/index.graphql';

const UPDATE_SECTION_NOTIFICATION_PREFERENCES_MUTATION = gql`
  mutation UpdateSectionNotificationPreferencesMutation(
    $input: updateSectionNotificationPreferencesInput!
    $sportOrShared: SportOrShared!
  ) {
    updateSectionNotificationPreferences(input: $input) {
      section {
        slug
        name
        myNotificationPreferences(sport: $sportOrShared) {
          name
          channels
        }
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdateSectionNotificationPreferencesMutation,
  UpdateSectionNotificationPreferencesMutationVariables
>;

type Props = {
  notificationSection: NotificationSection_notificationSection;
  sport: SportOrShared;
  onChange: (
    name: string,
    newValue: NotificationPreferenceChannel[] | Json
  ) => void;
};

export const NotificationSection = ({
  notificationSection,
  sport,
  onChange,
}: Props) => {
  const [mutate] = useMutation(
    UPDATE_SECTION_NOTIFICATION_PREFERENCES_MUTATION
  );

  if (notificationSection.myNotificationPreferences.length === 0) {
    return null;
  }

  const hasAtLeastOnePreferenceEnabled =
    notificationSection.myNotificationPreferences.some(preference => {
      const enabledChannels = preference.channels ?? preference.defaultChannels;

      return enabledChannels.length > 0;
    });

  return (
    <Section
      title={notificationSection.name}
      actions={
        <ButtonBase
          onClick={() => {
            mutate({
              variables: {
                sportOrShared: sport,
                input: {
                  section: notificationSection.slug,
                  sport,
                  channels: hasAtLeastOnePreferenceEnabled
                    ? []
                    : Object.values(NotificationPreferenceChannel),
                },
              },
            });
          }}
        >
          <LabelM bold color="var(--c-nd-600)">
            {hasAtLeastOnePreferenceEnabled ? (
              <FormattedMessage
                id="Settings.Notifications.disableAll"
                defaultMessage="Disable all"
              />
            ) : (
              <FormattedMessage
                id="Settings.Notifications.enableAll"
                defaultMessage="Enable all"
              />
            )}
          </LabelM>
        </ButtonBase>
      }
    >
      {notificationSection.myNotificationPreferences.map(preference => (
        <NotificationPreference
          key={`${preference.name}-${sport}`}
          preference={preference}
          onChange={newValue => {
            onChange(preference.name, newValue);
          }}
        />
      ))}
    </Section>
  );
};

NotificationSection.fragments = {
  notificationSection: gql`
    fragment NotificationSection_notificationSection on NotificationSection {
      slug
      name
      myNotificationPreferences(sport: $sportOrShared) {
        name
        channels
        defaultChannels
        ...NotificationPreference_notificationPreference
      }
    }
    ${NotificationPreference.fragments.notificationPreference}
  ` as TypedDocumentNode<NotificationSection_notificationSection>,
};
